/**
 * Product personalisation add-ons: grouped multi-add + cart sorting/cleanup helpers.
 *
 * Why this file exists:
 * - The theme's default add-to-cart handler (`theme.AjaxProduct`) adds a single variant.
 * - Personalisation requires adding the main product AND selected add-on variants together,
 *   and tagging them with a shared group UID so cart removal/sorting can treat them as one.
 *
 * Grouping contract (doc keys):
 * - `_groupUID`: shared unique id across main + add-ons
 * - `_isMainItem`: 'true' only on the main line
 * - `_isAddon`: 'true' only on add-on lines
 * - `_addonType`: badge|sponsor|player|naam|nummer
 */
(function () {
  const selectors = {
    productForm: '.product-single__form',
    addToCartBtn: '[data-add-to-cart]',
    variantSelect: 'select[name="id"]',
    productTitleEl: '[data-product-title]',
    personalisationFlagContainers: ['.product-addon-container-gm', '.product-academy-personalisation'],

    modeButton: '.personalisation-button.selected[data-target]',
    playerSelect: '.player-select',
    nameInput: '.naam-input',
    numberInput: '.nummer-input',

    badgeButtons: '#badge-product-addon .product-addon-buttons[data-option="badge"]',
    sponsorButtons: '#sponsor-sleeve-product-addon .product-addon-buttons[data-option="sponsor"]',
  };

  function generateGroupUID() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return `pg-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function getVariantIdFromForm(form) {
    const el = form.querySelector(selectors.variantSelect);
    const raw = el && el.value;
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }

  function getProductDetailsForCart() {
    const model =
      document.querySelector(selectors.productTitleEl)?.getAttribute('data-product-title') ||
      document.querySelector(selectors.productTitleEl)?.textContent ||
      'Onbekend';

    const select = document.querySelector(selectors.variantSelect);
    const selectedOptionText = select?.selectedOptions?.[0]?.textContent || '';
    const sizeCandidate = selectedOptionText.split('-')[0]?.trim();

    return {
      model: (model || '').trim(),
      size: (sizeCandidate || '').trim() || (selectedOptionText || '').trim() || 'Onbekend',
    };
  }

  function readToggleSelectedValue(toggleButtonsEl) {
    // Expect markup contract: .product-addon-buttons[data-selected="no|yes"]
    const selected = toggleButtonsEl?.getAttribute('data-selected');
    if (selected === 'yes' || selected === 'no') return selected;
    return 'no';
  }

  function getAddonVariantIdFromEl(el) {
    const raw = el?.dataset?.productId;
    const parsed = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }

  async function openCartDrawerAndRefresh() {
    const cartButton = document.querySelector('.js-drawer-open-cart');
    if (cartButton) {
      cartButton.click();
      // Allow drawer animation + ajax pipeline to initialize.
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
      }, 300);
      return;
    }
    window.location.href = '/cart';
  }

  async function cleanupOrphanedAddons() {
    // Prefer theme helper if present (keeps logic centralized once theme.js is updated).
    if (window.theme?.cart?.cleanupOrphanedAddons) {
      return window.theme.cart.cleanupOrphanedAddons();
    }
  }

  function setAddonToggleSelected(toggleButtonsEl, value) {
    if (!toggleButtonsEl) return;
    if (value !== 'yes' && value !== 'no') return;

    toggleButtonsEl.setAttribute('data-selected', value);
    toggleButtonsEl.querySelectorAll('.product-addon-button').forEach((btn) => {
      const btnValue = btn.getAttribute('data-value');
      btn.classList.toggle('selected', btnValue === value);
    });
  }

  function setMode(target) {
    const buttons = document.querySelectorAll('.personalisation-button[data-target]');
    if (!buttons.length) return;

    buttons.forEach((btn) => {
      btn.classList.toggle('selected', btn.getAttribute('data-target') === target);
    });

    const playerSection = document.querySelector('.product-addon-player-item');
    const nameNumberSection = document.querySelector('.product-addon-naamnummer-container');

    if (playerSection) playerSection.classList.toggle('hidden', target !== 'player');
    if (nameNumberSection) nameNumberSection.classList.toggle('hidden', target !== 'naam-nummer');

    // If mode is none, hide both.
    if (target === 'none') {
      if (playerSection) playerSection.classList.add('hidden');
      if (nameNumberSection) nameNumberSection.classList.add('hidden');
    }
  }

  function bindAddonUi() {
    const container = document.querySelector('.product-addon-container-gm');
    if (!container || container.dataset.personalisationUiBound === 'true') return;
    container.dataset.personalisationUiBound = 'true';

    // Mode buttons (none/player; naam-nummer markup may be omitted on some PDPs)
    container.addEventListener('click', (e) => {
      const button = e.target.closest('.personalisation-button[data-target]');
      if (!button) return;

      e.preventDefault();
      const target = button.getAttribute('data-target') || 'none';
      setMode(target);
    });

    // Yes/No toggle groups (badge, sponsor, etc.)
    container.addEventListener('click', (e) => {
      const button = e.target.closest('.product-addon-button[data-value]');
      if (!button) return;

      const toggleButtonsEl = button.closest('.product-addon-buttons[data-selected][data-option]');
      if (!toggleButtonsEl) return;

      e.preventDefault();
      const value = button.getAttribute('data-value');
      setAddonToggleSelected(toggleButtonsEl, value);
    });

    // Ensure the initial UI state matches markup defaults.
    const initialTarget =
      document.querySelector('.personalisation-button.selected[data-target]')?.getAttribute('data-target') || 'none';
    setMode(initialTarget);

    setAddonToggleSelected(document.querySelector(selectors.badgeButtons), readToggleSelectedValue(document.querySelector(selectors.badgeButtons)));
    setAddonToggleSelected(document.querySelector(selectors.sponsorButtons), readToggleSelectedValue(document.querySelector(selectors.sponsorButtons)));
  }

  function sortCartItems() {
    // This runs on the rendered cart DOM, not the cart JSON.
    const container =
      document.querySelector('.cart__items') ||
      document.querySelector('[data-products]');
    if (!container) return;

    const children = Array.from(container.children);
    if (!children.length) return;

    const isMain = (node) =>
      node.classList.contains('cart__item--main') || node.getAttribute('data-is-main') === 'true';
    const getGroupUID = (node) => node.getAttribute('data-group-uid') || '';

    const mains = children.filter((node) => isMain(node) && getGroupUID(node));
    if (!mains.length) return;

    // Rebuild: each main followed by its addons; ungrouped at the end (stable order).
    const used = new Set();
    const fragments = [];

    mains.forEach((mainNode) => {
      const groupUID = getGroupUID(mainNode);
      fragments.push(mainNode);
      used.add(mainNode);

      children.forEach((maybeAddon) => {
        if (used.has(maybeAddon)) return;
        if (getGroupUID(maybeAddon) !== groupUID) return;
        if (!maybeAddon.classList.contains('cart__item--addon')) return;
        fragments.push(maybeAddon);
        used.add(maybeAddon);
      });
    });

    children.forEach((node) => {
      if (used.has(node)) return;
      fragments.push(node);
      used.add(node);
    });

    fragments.forEach((node) => container.appendChild(node));
  }

  async function addItemsToCart(form) {
    await cleanupOrphanedAddons();

    const mainVariantId = getVariantIdFromForm(form);
    if (!mainVariantId) return;

    const productDetails = getProductDetailsForCart();
    const groupUID = generateGroupUID();

    const mode = document.querySelector(selectors.modeButton)?.getAttribute('data-target') || 'none';

    const playerValue = document.querySelector(selectors.playerSelect)?.value?.trim() || '';
    const nameValue = document.querySelector(selectors.nameInput)?.value?.trim() || '';
    const numberValue = document.querySelector(selectors.numberInput)?.value?.trim() || '';

    const badgeSelected = readToggleSelectedValue(document.querySelector(selectors.badgeButtons));
    const sponsorSelected = readToggleSelectedValue(document.querySelector(selectors.sponsorButtons));

    const items = [];

    // Main item properties: private + customer-visible summary.
    const mainItemProperties = {
      _groupUID: groupUID,
      _isMainItem: 'true',
      Model: productDetails.model,
      Size: productDetails.size,
    };

    if (mode === 'player' && playerValue) {
      mainItemProperties.Speler = playerValue;
    }
    if (mode === 'naam-nummer') {
      if (nameValue) mainItemProperties.Naam = nameValue;
      if (numberValue) mainItemProperties.Nummer = numberValue;
    }
    if (badgeSelected === 'yes') mainItemProperties.Badge = 'Ja';
    if (sponsorSelected === 'yes') mainItemProperties.Sponsor = 'Ja';

    items.push({
      id: mainVariantId,
      quantity: 1,
      properties: mainItemProperties,
    });

    const pushAddon = (variantId, addonType) => {
      if (!variantId) return;
      items.push({
        id: variantId,
        quantity: 1,
        properties: {
          _groupUID: groupUID,
          _isAddon: 'true',
          _addonType: addonType,
        },
      });
    };

    // Badge
    if (badgeSelected === 'yes') {
      const badgeVariantId = getAddonVariantIdFromEl(document.querySelector('#badge-product-addon'));
      pushAddon(badgeVariantId, 'badge');
    }

    // Sponsor sleeve
    if (sponsorSelected === 'yes') {
      const sponsorVariantId = getAddonVariantIdFromEl(document.querySelector('#sponsor-sleeve-product-addon'));
      pushAddon(sponsorVariantId, 'sponsor');
    }

    // Player
    if (mode === 'player' && playerValue) {
      const playerVariantId =
        getAddonVariantIdFromEl(document.querySelector(selectors.playerSelect)) ||
        getAddonVariantIdFromEl(document.querySelector('.product-addon-player-item'));
      pushAddon(playerVariantId, 'player');
    }

    // Name / number
    if (mode === 'naam-nummer') {
      if (nameValue) {
        const nameVariantId = getAddonVariantIdFromEl(document.querySelector(selectors.nameInput));
        pushAddon(nameVariantId, 'naam');
      }
      if (numberValue) {
        const numberVariantId = getAddonVariantIdFromEl(document.querySelector(selectors.numberInput));
        pushAddon(numberVariantId, 'nummer');
      }
    }

    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items }),
    });

    if (!res.ok) {
      // Keep errors visible during implementation so we can diagnose incorrect variant IDs quickly.
      // eslint-disable-next-line no-console
      console.error('Personalisation: /cart/add.js failed', await res.text());
      throw new Error('Personalisation add to cart failed');
    }

    await openCartDrawerAndRefresh();
  }

  function shouldActivateOnPage() {
    return selectors.personalisationFlagContainers.some((sel) => document.querySelector(sel));
  }

  function bindFormSubmit(form) {
    if (!form || form.dataset.personalisationBound === 'true') return;
    form.dataset.personalisationBound = 'true';

    form.addEventListener(
      'submit',
      async (e) => {
        // Only intercept when the personalisation UI exists.
        if (!shouldActivateOnPage()) return;

        e.preventDefault();
        // Critical: stop the theme's AjaxProduct handler from also running.
        e.stopImmediatePropagation();

        const addToCartBtn = form.querySelector(selectors.addToCartBtn);
        if (addToCartBtn) {
          addToCartBtn.disabled = true;
          addToCartBtn.classList.add('btn--loading');
        }

        try {
          await addItemsToCart(form);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
          alert('Er is iets mis gegaan bij het toevoegen van de items.');
        } finally {
          if (addToCartBtn) {
            addToCartBtn.disabled = false;
            addToCartBtn.classList.remove('btn--loading');
          }
        }
      },
      { capture: true },
    );
  }

  function init() {
    if (shouldActivateOnPage()) {
      bindAddonUi();
    }

    const form = document.querySelector(selectors.productForm);
    if (form) bindFormSubmit(form);

    // Whenever the cart UI rebuilds, ensure grouped addons stay underneath their main line.
    document.addEventListener('cart:updated', () => {
      sortCartItems();
    });
    document.addEventListener('cart:build', () => {
      // cart:build triggers an async rebuild; run a delayed sort as a safety net.
      setTimeout(() => sortCartItems(), 500);
    });
  }

  window.ProductPersonalisation = {
    init,
    sortCartItems,
    cleanupOrphanedAddons,
  };

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', () => setTimeout(init, 50));
})();

